import React, { useMemo, useRef, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { LocalizationProvider, DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { COLORS, FONT_FAMILY, FONT_HEADING } from './constants/theme';
const naturalCompare = (a, b) =>
  String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

function getSchoolOptions(sessions) {
  return [...new Set(sessions.map((s) => s.school).filter(Boolean))].sort(naturalCompare);
}

function getPeriodOptions(sessions) {
  return [...new Set(sessions.map((s) => s.period).filter(Boolean))].sort(naturalCompare);
}

function matchesNmFilter(session, { schools, periods }) {
  if (schools.size > 0 && !schools.has(session.school)) return false;
  if (periods.size > 0 && !periods.has(session.period)) return false;
  return true;
}

function summarizeSelection(selected, emptyLabel) {
  const values = [...selected].sort(naturalCompare);
  if (values.length === 0) return emptyLabel;
  if (values.length <= 2) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} +${values.length - 2} more`;
}

const GREY_DARK = COLORS.text;
const GREY_MED = COLORS.textMuted;
const GREY_TINT = '#F3F4F6';

const RADII = { panel: 16, card: 14, control: 8, pill: 4 };

const withAlpha = (color, alpha) => {
  if (typeof color !== 'string') return color;
  let hex = color.trim();
  if (hex[0] !== '#') return hex;
  if (hex.length === 4) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  if (hex.length !== 7) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BRAND = COLORS.primary;
const BRAND_RANGE = withAlpha(BRAND, 0.16);
const BRAND_TINT = withAlpha(BRAND, 0.08);

const ymd = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function computeDayState(date, rangeStart, rangeEnd) {
  const d = startOfDay(date);
  if (!rangeStart) return { selected: false, inRange: false, isStart: false, isEnd: false };
  const isStart = +d === +rangeStart;
  const end = rangeEnd || rangeStart;
  const isEnd = +d === +end;
  const inRange = d >= rangeStart && d <= end;
  return { selected: isStart || isEnd, inRange, isStart, isEnd };
}

function CaretIcon({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"
      style={{ transition: 'transform .15s ease', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
      <path d="M4 6l4 4 4-4" stroke={GREY_DARK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 3.5h12M4.5 8h7M6.75 12.5h2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M3 8.5l3 3 7-7" stroke={BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchInput({ value, onChange, onClear }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      height: 36, padding: '0 12px', borderRadius: RADII.pill, boxSizing: 'border-box',
      border: `1px solid ${value ? BRAND : COLORS.border}`,
      background: COLORS.page, width: '100%',
    }}>
      <span style={{ color: value ? BRAND : GREY_MED, display: 'inline-flex', flexShrink: 0 }}><SearchIcon /></span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="Search by title"
        aria-label="Search sessions by title"
        style={{
          border: 'none', outline: 'none', background: 'transparent',
          fontSize: 14, fontFamily: FONT_FAMILY, color: COLORS.text, width: '100%',
        }}
      />
      {value && (
        <button type="button" onClick={onClear} aria-label="Clear search"
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: GREY_MED, display: 'flex', padding: 0, flexShrink: 0 }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function RangeDay(props) {
  const { day, outsideCurrentMonth, sessionDateKeys, rangeStart, rangeEnd, onDayClick, ...other } = props;
  const jsDate = day.toDate();
  const key = ymd(jsDate);
  const hasSession = sessionDateKeys.has(key);
  const st = computeDayState(jsDate, rangeStart, rangeEnd);
  const solid = st.isStart || st.isEnd || (st.selected && !st.inRange);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {st.inRange && !outsideCurrentMonth && (
        <Box aria-hidden="true" sx={{
          position: 'absolute', top: 3, bottom: 3,
          left: st.isStart ? '50%' : 0, right: st.isEnd ? '50%' : 0,
          background: BRAND_RANGE, zIndex: 0,
        }} />
      )}
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disableMargin
        onClick={() => onDayClick(jsDate)}
        selected={solid}
        sx={{
          position: 'relative', zIndex: 1,
          fontWeight: hasSession || solid ? 600 : 400,
          ...(solid && {
            backgroundColor: `${BRAND} !important`,
            color: '#FFFFFF !important',
          }),
        }}
      />
      {hasSession && !outsideCurrentMonth && (
        <Box aria-hidden="true" sx={{
          position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)',
          width: 5, height: 5, borderRadius: '50%',
          background: solid ? '#FFFFFF' : BRAND,
          zIndex: 2, pointerEvents: 'none',
        }} />
      )}
    </Box>
  );
}

function FilterSection({ title, summary, note, open, onToggle, onAction, actionLabel, children }) {
  return (
    <div style={{
      marginTop: 20,
      border: `1.5px solid ${COLORS.border}`,
      borderRadius: RADII.card,
      overflow: 'hidden',
      background: COLORS.page,
      boxShadow: open ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
      transition: 'box-shadow .15s ease',
    }}>
      <div
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          alignItems: 'center',
          columnGap: 10,
          rowGap: 6,
          background: GREY_TINT,
          borderBottom: open ? `1.5px solid ${COLORS.border}` : 'none',
          padding: '11px 14px',
          fontFamily: FONT_FAMILY,
        }}
      >
        <button
          type="button" onClick={onToggle} aria-expanded={open}
          style={{
            minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2,
            background: 'none', border: 'none', padding: 0, margin: 0,
            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
          }}
        >
          <span style={{
            fontSize: 12, fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase',
            color: GREY_DARK,
          }}>
            {title}
          </span>
          {summary && (
            <span style={{
              fontSize: 11.5, fontWeight: 600, color: COLORS.text,
              overflowWrap: 'break-word', whiteSpace: 'normal', lineHeight: 1.35,
            }}>
              {summary}
            </span>
          )}
        </button>

        {onAction ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAction(); }}
            style={{
              background: 'none', border: 'none', color: BRAND, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, padding: '4px 6px',
              textDecoration: 'underline',
            }}
          >
            {actionLabel}
          </button>
        ) : <span />}

        <button
          type="button" onClick={onToggle} aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, display: 'flex' }}
        >
          <CaretIcon open={open} />
        </button>

        {note && (
          <span style={{
            gridColumn: '1 / -1',
            justifySelf: 'start',
            textAlign: 'left',
            fontSize: 11.5, lineHeight: 1.4, color: GREY_MED,
            whiteSpace: 'normal',
          }}>
            {note}
          </span>
        )}
      </div>
      {open && (
        <div style={{ padding: '5px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function CheckList({ options, selected, onToggle, ariaLabel, emptyText }) {
  if (options.length === 0) {
    return <div style={{ fontSize: 13, color: GREY_MED, padding: '8px 4px' }}>{emptyText}</div>;
  }
  return (
    <div className="ttf-list" role="listbox" aria-multiselectable="true" aria-label={ariaLabel}>
      {options.map((opt) => {
        const sel = selected.has(opt);
        return (
          <button
            key={opt}
            type="button"
            role="option"
            aria-selected={sel}
            onClick={() => onToggle(opt)}
            className={`ttf-list-row${sel ? ' ttf-list-row--sel' : ''}`}
          >
            <span style={{ lineHeight: 1.2 }}>{opt}</span>
            {sel && <CheckIcon />}
          </button>
        );
      })}
    </div>
  );
}

const toggleInSet = (prev, value) => {
  const next = new Set(prev);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

export default function TreeLibraryFilters({ sessions = [], onFilterChange, children, heading, actions, nmMode = false }) {
  const { sessionDateKeys, sessionMeta } = useMemo(() => {
    const dateKeys = new Set();
    const meta = sessions.map((s) => {
      const date = s.startTime ? startOfDay(new Date(s.startTime)) : null;
      if (date) dateKeys.add(ymd(date));
      return {
        sessionId: s.sessionId,
        date,
        title: s.title || '',
        school: s.school || '',
        period: s.period || '',
      };
    });
    return { sessionDateKeys: dateKeys, sessionMeta: meta };
  }, [sessions]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(true);
  const [listOpen, setListOpen] = useState(true);
  const [schoolOpen, setSchoolOpen] = useState(true);
  const [periodOpen, setPeriodOpen] = useState(true);

  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [selSessions, setSelSessions] = useState(new Set());
  const [titleQuery, setTitleQuery] = useState('');

  const [nmSchools, setNmSchools] = useState(new Set());
  const [nmPeriods, setNmPeriods] = useState(new Set());

  const initialCalendarDate = useMemo(
    () => dayjs(sessionMeta.find((m) => m.date)?.date || new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [viewMonth, setViewMonth] = useState(initialCalendarDate);
  const monthRowCount = (d) => {
    const firstWeekday = d.startOf('month').day();
    const daysInMonth = d.daysInMonth();
    return Math.ceil((firstWeekday + daysInMonth) / 7);
  };
  const calendarMinHeight = monthRowCount(viewMonth) * 40 + 4;

  const lastEmitted = useRef(undefined);

  const listRef = useRef(null);
  const [listScroll, setListScroll] = useState({ up: false, down: false });
  const updateListScroll = () => {
    const el = listRef.current;
    const up = el ? el.scrollTop > 2 : false;
    const down = el ? el.scrollTop + el.clientHeight < el.scrollHeight - 2 : false;
    setListScroll((prev) => (prev.up === up && prev.down === down ? prev : { up, down }));
  };
  const scrollList = (dir) => listRef.current?.scrollBy({ top: dir * 140, behavior: 'smooth' });

  const schoolOptions = useMemo(() => {
    const scoped = nmPeriods.size === 0
      ? sessions
      : sessions.filter((s) => nmPeriods.has(s.period));
    return getSchoolOptions(scoped.map((s) => ({ school: s.school })));
  }, [sessions, nmPeriods]);
  const periodOptions = useMemo(() => {
    const scoped = nmSchools.size === 0
      ? sessions
      : sessions.filter((s) => nmSchools.has(s.school));
    return getPeriodOptions(scoped.map((s) => ({ period: s.period })));
  }, [sessions, nmSchools]);

  useEffect(() => {
    if (!nmMode) return;
    setNmPeriods((prev) => {
      const allowed = new Set(periodOptions);
      const next = new Set([...prev].filter((p) => allowed.has(p)));
      return next.size === prev.size ? prev : next;
    });
  }, [periodOptions, nmMode]);
  useEffect(() => {
    if (!nmMode) return;
    setNmSchools((prev) => {
      const allowed = new Set(schoolOptions);
      const next = new Set([...prev].filter((s) => allowed.has(s)));
      return next.size === prev.size ? prev : next;
    });
  }, [schoolOptions, nmMode]);

  const anyDateFilter = !!rangeStart;
  const anySessionFilter = selSessions.size > 0;
  const anyNmFilter = nmSchools.size > 0 || nmPeriods.size > 0;
  const anyFilter = anyDateFilter || anySessionFilter || anyNmFilter || titleQuery.trim().length > 0;

  const allTreesSelected = selSessions.size === 0;

  const handleDayClick = (date) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(startOfDay(date)); setRangeEnd(null);
      mixpanel.track('treeLibraryDateRangeSelected', { stage: 'start', date: ymd(startOfDay(date)) });
    } else {
      let start = rangeStart, end = startOfDay(date);
      if (end < start) [start, end] = [end, start];
      setRangeStart(start); setRangeEnd(end);
      mixpanel.track('treeLibraryDateRangeSelected', { stage: 'completed', startDate: ymd(start), endDate: ymd(end) });
    }
  };

  const clearDateRange = () => {
    mixpanel.track('treeLibraryDateRangeCleared');
    setRangeStart(null); setRangeEnd(null);
  };
  const selectAllTrees = () => {
    mixpanel.track('treeLibrarySelectAllTreesClicked');
    setSelSessions(new Set());
  };
  const clearNmFilters = () => {
    mixpanel.track('treeLibraryNmFiltersCleared');
    setNmSchools(new Set());
    setNmPeriods(new Set());
  };
  const clearAll = () => {
    mixpanel.track('treeLibraryClearAllFiltersClicked');
    clearDateRange(); selectAllTrees(); setTitleQuery(''); clearNmFilters();
  };

  const toggleSession = (id) => setSelSessions((prev) => {
    if (prev.size === 0) {
      const all = new Set(sessionMeta.map((m) => m.sessionId));
      all.delete(id);
      return all;
    }
    const n = new Set(prev);
    if (n.has(id)) {
      if (n.size === 1) return prev;
      n.delete(id);
    } else {
      n.add(id);
    }
    return n.size === sessionMeta.length ? new Set() : n;
  });

  const passesBase = (m) => {
    if (rangeStart) {
      if (!m.date) return false;
      const end = rangeEnd || rangeStart;
      if (m.date < rangeStart || m.date > end) return false;
    }
    if (titleQuery.trim() && !m.title.toLowerCase().includes(titleQuery.trim().toLowerCase())) return false;
    return true;
  };

  const passesSelection = (m) => {
    if (nmMode) return matchesNmFilter(m, { schools: nmSchools, periods: nmPeriods });
    return selSessions.size === 0 || selSessions.has(m.sessionId);
  };

  useEffect(() => {
    const pass = sessionMeta.filter((m) => passesBase(m) && passesSelection(m));
    const ids = pass.map((m) => m.sessionId);
    const sig = ids.join('|');
    if (lastEmitted.current !== sig) {
      lastEmitted.current = sig;
      onFilterChange?.(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rangeStart, rangeEnd, JSON.stringify([...selSessions]), titleQuery, sessions,
    nmMode, JSON.stringify([...nmSchools]), JSON.stringify([...nmPeriods]),
  ]);

  useEffect(() => {
    updateListScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Debounced search tracking: fires once ~600ms after the user stops typing,
  // rather than on every keystroke. Skips the very first render (empty query,
  // nothing to report) and skips firing when the query is cleared to empty
  // (that's covered by the explicit clear-button click below).
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    if (!titleQuery.trim()) return;
    const timer = setTimeout(() => {
      mixpanel.track('treeLibrarySearchApplied', { queryLength: titleQuery.trim().length });
    }, 600);
    return () => clearTimeout(timer);
  }, [titleQuery]);

  const formatFullDate = (d) => d ? d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'No date';

  const listGroups = useMemo(() => {
    const groups = new Map();
    sessionMeta.filter(passesBase).forEach((m) => {
      const key = m.date ? ymd(m.date) : 'unknown';
      if (!groups.has(key)) groups.set(key, { key, date: m.date, items: [] });
      groups.get(key).items.push(m);
    });
    return [...groups.values()].sort((a, b) => (b.date ? +b.date : 0) - (a.date ? +a.date : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionMeta, rangeStart, rangeEnd, titleQuery]);

  const listOverflows = listScroll.up || listScroll.down;

  const scrollArrow = (dir) => {
    const enabled = dir < 0 ? listScroll.up : listScroll.down;
    return (
      <button type="button" onClick={() => scrollList(dir)} disabled={!enabled}
        aria-label={dir < 0 ? 'Scroll up' : 'Scroll down'}
        style={{
          width: '100%', height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', background: 'transparent', cursor: enabled ? 'pointer' : 'default',
          color: enabled ? GREY_MED : COLORS.disabled,
        }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={dir < 0 ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };

  const activeFilterCount =
    (anyDateFilter ? 1 : 0) +
    (anySessionFilter ? 1 : 0) +
    (nmMode && nmSchools.size > 0 ? 1 : 0) +
    (nmMode && nmPeriods.size > 0 ? 1 : 0) +
    (titleQuery.trim() ? 1 : 0);

  const dateRangeSummary = rangeStart
    ? (rangeEnd
      ? `${rangeStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${rangeEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
      : rangeStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))
    : 'No date selected';
  const listSelectionSummary = allTreesSelected
    ? 'All sessions are selected. Click any title to hide its tree.'
    : `${selSessions.size} selected`;

  const toggleNmSchool = (school) => {
    mixpanel.track('treeLibrarySchoolToggled', { school });
    setNmSchools((prev) => toggleInSet(prev, school));
  };
  const togglePeriod = (period) => {
    mixpanel.track('treeLibraryPeriodToggled', { period });
    setNmPeriods((prev) => toggleInSet(prev, period));
  };

  return (
    <div style={{ fontFamily: FONT_FAMILY }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => setSidebarOpen((o) => {
              const next = !o;
              mixpanel.track('treeLibraryFilterPanelToggled', { opened: next });
              return next;
            })}
            aria-expanded={sidebarOpen}
            aria-controls="ttf-sidebar-panel"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              height: 36, padding: '0 14px', borderRadius: RADII.pill, cursor: 'pointer',
              fontSize: 14, fontWeight: 600, fontFamily: FONT_FAMILY,
              border: sidebarOpen ? `1px solid ${BRAND}` : `1px solid ${COLORS.border}`,
              background: sidebarOpen ? BRAND : COLORS.page,
              color: sidebarOpen ? '#fff' : COLORS.text,
              transition: 'background .15s ease, border-color .15s ease, color .15s ease',
            }}
          >
            <FilterIcon />
            Filter
            {activeFilterCount > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700, lineHeight: 1, padding: '2px 6px', borderRadius: 10,
                background: sidebarOpen ? 'rgba(255,255,255,0.25)' : BRAND_TINT,
                color: sidebarOpen ? '#fff' : BRAND,
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {heading && (
            <span style={{ fontSize: 20, fontWeight: 600, color: COLORS.text, fontFamily: FONT_HEADING }}>
              {heading}
            </span>
          )}
        </div>
        {actions && <div style={{ display: 'flex', alignItems: 'center' }}>{actions}</div>}
      </div>

      <div className="ttf-shell">
        {sidebarOpen && (
          <aside id="ttf-sidebar-panel" className="ttf-sidebar">

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Filters</span>
              {anyFilter && (
                <button type="button" onClick={clearAll}
                  style={{ background: 'none', border: 'none', color: BRAND, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_FAMILY }}>
                  Clear all
                </button>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <SearchInput
                value={titleQuery}
                onChange={setTitleQuery}
                onClear={() => {
                  mixpanel.track('treeLibrarySearchCleared');
                  setTitleQuery('');
                }}
              />
            </div>

            <FilterSection
              title="Date"
              summary={dateRangeSummary}
              open={dateOpen}
              onToggle={() => setDateOpen((o) => {
                const next = !o;
                mixpanel.track('treeLibraryFilterSectionToggled', { section: 'Date', opened: next });
                return next;
              })}
              onAction={anyDateFilter ? clearDateRange : null}
              actionLabel="Clear dates"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  referenceDate={initialCalendarDate}
                  value={null}
                  onChange={() => { }}
                  onMonthChange={(d) => setViewMonth(d)}
                  views={['day']}
                  slots={{ day: RangeDay }}
                  slotProps={{
                    day: { sessionDateKeys, rangeStart, rangeEnd, onDayClick: handleDayClick },
                  }}
                  sx={{
                    width: 260,
                    m: 0,
                    height: 'auto',
                    '& .MuiPickersCalendarHeader-root': {
                      minHeight: 32, paddingLeft: 0.5, paddingRight: 0.5, marginTop: 0, marginBottom: 0.5,
                    },
                    '& .MuiPickersCalendarHeader-label': { fontSize: 13, fontWeight: 600, fontFamily: FONT_FAMILY, color: COLORS.text },
                    '& .MuiPickersArrowSwitcher-button': { padding: '4px', color: GREY_DARK },
                    '& .MuiDayCalendar-weekDayLabel': { width: 32, height: 24, fontSize: 11, color: GREY_MED },
                    '& .MuiPickersDay-root': { width: 32, height: 32, fontSize: 13, margin: '1px', color: COLORS.text },
                    '& .MuiPickersDay-root:hover': { backgroundColor: BRAND_TINT },
                    '& .MuiDayCalendar-weekContainer': { margin: '3px 0' },
                    '& .MuiDayCalendar-slideTransition': { minHeight: calendarMinHeight },
                    '& .MuiDateCalendar-viewTransitionContainer': { minHeight: 'auto' },
                  }}
                />
              </LocalizationProvider>
            </FilterSection>

            {nmMode ? (
              <>
                <FilterSection
                  title="School"
                  summary={summarizeSelection(nmSchools, 'All schools')}
                  note={nmPeriods.size > 0 ? 'Showing schools for the selected periods.' : null}
                  open={schoolOpen}
                  onToggle={() => setSchoolOpen((o) => !o)}
                  onAction={nmSchools.size > 0 ? () => setNmSchools(new Set()) : null}
                  actionLabel="Clear"
                >
                  <CheckList
                    options={schoolOptions}
                    selected={nmSchools}
                    onToggle={toggleNmSchool}
                    ariaLabel="Schools"
                    emptyText="No schools for the selected periods."
                  />
                </FilterSection>

                <FilterSection
                  title="Period"
                  summary={summarizeSelection(nmPeriods, 'All periods')}
                  note={nmSchools.size > 0 ? 'Showing periods for the selected schools.' : null}
                  open={periodOpen}
                  onToggle={() => setPeriodOpen((o) => !o)}
                  onAction={nmPeriods.size > 0 ? () => setNmPeriods(new Set()) : null}
                  actionLabel="Clear"
                >
                  <CheckList
                    options={periodOptions}
                    selected={nmPeriods}
                    onToggle={togglePeriod}
                    ariaLabel="Periods"
                    emptyText="No periods for the selected schools."
                  />
                </FilterSection>
              </>
            ) : (
              <FilterSection
                title="Select Trees"
                summary={listSelectionSummary}
                open={listOpen}
                onToggle={() => setListOpen((o) => {
                  const next = !o;
                  mixpanel.track('treeLibraryFilterSectionToggled', { section: 'Select Trees', opened: next });
                  return next;
                })}
                onAction={anySessionFilter ? selectAllTrees : null}
                actionLabel="Select all"
              >
                {listOverflows && scrollArrow(-1)}
                <div className="ttf-list" ref={listRef} onScroll={updateListScroll} role="listbox" aria-label="Sessions by date">
                  {listGroups.length === 0 && (
                    <div style={{ fontSize: 13, color: GREY_MED, padding: '8px 4px' }}>No sessions match.</div>
                  )}
                  {listGroups.map((g) => (
                    <div key={g.key} style={{ marginBottom: 12 }}>
                      <div className="ttf-list-date">{formatFullDate(g.date)}</div>
                      <div>
                        {g.items.map((it) => {
                          const sel = allTreesSelected ? true : selSessions.has(it.sessionId);
                          return (
                            <button key={it.sessionId} type="button" role="option" aria-selected={sel}
                              onClick={() => {
                                mixpanel.track('treeLibrarySessionToggled', {
                                  sessionId: it.sessionId,
                                  action: sel ? 'deselected' : 'selected',
                                });
                                toggleSession(it.sessionId);
                              }}
                              className={`ttf-list-row${sel ? ' ttf-list-row--sel' : ''}`}>
                              <span style={{ lineHeight: 1.2 }}>
                                {it.title || 'Session'}
                              </span>
                              {sel && <CheckIcon />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {listOverflows && scrollArrow(1)}
              </FilterSection>
            )}
          </aside>
        )}

        <main className="ttf-main">{children}</main>
      </div>

      <style>{`
        .ttf-shell { display: flex; gap: 20px; align-items: flex-start; }
        .ttf-sidebar { flex: 0 0 300px; max-width: 300px; box-sizing: border-box;
          border: 1px solid ${COLORS.hairline}; border-radius: ${RADII.panel}px; background: ${COLORS.page};
          box-shadow: 0 1px 3px rgba(0,0,0,0.04); padding: 18px;
           overflow-y: auto; }
        .ttf-main { flex: 1 1 auto; min-width: 0; }

        .ttf-list {
          max-height: 260px;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .ttf-list::-webkit-scrollbar { display: none; }

        .ttf-list-date {
          font-size: 12px; font-weight: 700; color: ${GREY_MED};
          text-transform: uppercase; letter-spacing: .3px;
          margin: 2px 0 6px; text-align: left;
        }
        .ttf-list-row {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;
          width: 90%; box-sizing: border-box; text-align: left;
          overflow-wrap: break-word; word-break: break-word; white-space: normal;
          font-family: ${FONT_FAMILY}; font-size: 14px; color: ${COLORS.text};
          background: ${COLORS.page}; border: 1px solid ${COLORS.border}; border-radius: ${RADII.control}px;
          padding: 6px 10px; margin-bottom: 5px; cursor: pointer;
          transition: border-color .15s ease, background .15s ease;
        }
        .ttf-list-row:hover { border-color: ${BRAND}; }
        .ttf-list-row--sel { border-color: ${BRAND}; background: ${BRAND_TINT}; font-weight: 600; }

        @media (max-width: 900px) {
          .ttf-shell { flex-direction: column; }
          .ttf-sidebar { flex: 1 1 auto; max-width: 100%; width: 100%; height: auto; }
        }
      `}</style>
    </div>
  );
}